import React, { useMemo } from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import {
  SortableListDropTarget,
  SortableListDropTargetProps
} from '../SortableListDropTarget/SortableListDropTarget';
import {
  RenderSortableListItem,
  SortableListItem
} from '../SortableListItem/SortableListItem';

export type SortableListProps<T> = PropsWithoutChildren<{
  items: Array<T>;
  renderItem: RenderSortableListItem<T>;
  getItemKey: (item: T) => string;
  onItemSorted: (sortInfo: {
    item: T;
    previousItem: T | null;
    nextItem: T | null;
  }) => void;
}>;

export const SortableList = React.memo(<T,>(props: SortableListProps<T>) => {
  const { items, renderItem, getItemKey, onItemSorted } = prepareProps({
    props,
    ignoreChildren: true
  });
  const dndType = useMemo(() => Symbol('SortableList'), []);

  const handleDrop: SortableListDropTargetProps<T>['onDrop'] = useEventCallback(
    (dropInfo) => {
      // item is inserted into the same position
      if (
        dropInfo.item === dropInfo.previousItem ||
        dropInfo.item === dropInfo.nextItem
      ) {
        return;
      }

      onItemSorted({
        item: dropInfo.item,
        nextItem: dropInfo.nextItem,
        previousItem: dropInfo.previousItem
      });
    }
  );

  return (
    <>
      <SortableListDropTarget
        dndType={dndType}
        previousItem={null}
        nextItem={items[0] ?? null}
        onDrop={handleDrop}
        data-testid="SortableList.DropTarget"
      ></SortableListDropTarget>
      {items.map((item, index) => {
        return (
          <React.Fragment key={getItemKey(item)}>
            <SortableListItem
              item={item}
              renderItem={renderItem}
              dndType={dndType}
            ></SortableListItem>
            <SortableListDropTarget
              dndType={dndType}
              previousItem={item}
              nextItem={items[index + 1] ?? null}
              onDrop={handleDrop}
              data-testid="SortableList.DropTarget"
            ></SortableListDropTarget>
          </React.Fragment>
        );
      })}
    </>
  );
}) as <T>(props: SortableListProps<T>) => JSX.Element;
